/**
 * The Notices list, grouped by the person the work is for. One hire's
 * onboarding is a conversation of its own, and a list in time order scatters
 * it among everybody else's; grouped, each hire reads as one place, the cards
 * that need the reader first and what merely happened after.
 */

import type { Notice } from "@utuwrk/core";

export interface NoticeGroup {
  /** The person the cards are for; "Other" for a notice whose workflow is gone. */
  name: string;
  needsYou: Notice[];
  happened: Notice[];
  /** The latest notice id in the group, so the group with the latest news leads. */
  newest: number;
  /** How many of the needs-you ones were unread when the list was opened. */
  fresh: number;
}

export const OTHER = "Other";
export const SEVERAL = "Across several people";

export function groupNotices(notices: readonly Notice[]): NoticeGroup[] {
  const groups = new Map<string, NoticeGroup>();
  for (const notice of notices) {
    const name = notice.several ? SEVERAL : (notice.employeeName ?? OTHER);
    const group = groups.get(name) ?? { name, needsYou: [], happened: [], newest: 0, fresh: 0 };
    (notice.tier === "needs-you" ? group.needsYou : group.happened).push(notice);
    group.newest = Math.max(group.newest, notice.id);
    if (notice.tier === "needs-you" && !notice.read) group.fresh += 1;
    groups.set(name, group);
  }
  return [...groups.values()].sort((a, b) => b.newest - a.newest);
}
